import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, ArrowRight, Building2, Users, Sprout } from "lucide-react";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import MarketingLayout from "../marketing/MarketingLayout";
import "../../styles/subscription.css";

const MONTHLY_PRICE = 50;
const ANNUAL_DISCOUNT = 0.17;
const ANNUAL_PRICE = Math.round(MONTHLY_PRICE * (1 - ANNUAL_DISCOUNT));
const MRP = 99;
const DISCOUNT_PCT = Math.round(((MRP - MONTHLY_PRICE) / MRP) * 100);
const MIN_MONTHLY = 1999;
const MIN_EMPLOYEES = 40;

const FREE_FEATURES = [
  "Employee management",
  "Basic attendance",
  "Basic leave management",
  "Employee self-service (basic)",
  "Basic reports",
];

const PREMIUM_FEATURES = [
  "All Brixigo modules",
  "Payroll & statutory compliance",
  "Attendance & leave management",
  "Reports & analytics",
  "Role-based access (IAM)",
  "Employee self-service",
  "Ongoing product updates",
];

const CUSTOM_FEATURES = [
  "All Premium features",
  "Custom workflows",
  "Advanced reporting",
  "Integrations (API, third-party)",
  "Multiple locations / departments",
  "Advanced IAM / SSO (if required)",
  "Dedicated support & SLA",
  "Industry-specific configuration",
];

const FEATURE_ROWS = [
  { label: "Payroll & payslips", free: true, premium: true, custom: true },
  { label: "Attendance", free: true, premium: true, custom: true },
  { label: "Leave management", free: true, premium: true, custom: true },
  { label: "Approval workflows", free: false, premium: true, custom: true },
  { label: "Shift & roster", free: false, premium: true, custom: true },
  { label: "Recruitment", free: false, premium: true, custom: true },
  { label: "Performance", free: false, premium: true, custom: true },
  { label: "Assets", free: false, premium: true, custom: true },
  { label: "API access", free: false, premium: false, custom: true },
  { label: "Audit logs", free: false, premium: true, custom: true },
  { label: "Organization branding", free: false, premium: false, custom: true },
  { label: "Custom workflows", free: false, premium: false, custom: true },
  { label: "Dedicated support & SLA", free: false, premium: false, custom: true },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const [billing, setBilling] = useState("monthly");

  const isAnnual = billing === "annual";
  const price = isAnnual ? ANNUAL_PRICE : MONTHLY_PRICE;
  const annualTotal = ANNUAL_PRICE * 12;

  const startSignup = (plan) => {
    if (user) {
      navigate("/settings/subscription?tab=plans");
      return;
    }
    navigate(`/registermodal?plan=${encodeURIComponent(plan)}`);
  };

  return (
    <MarketingLayout>
      <div className="sub-page sub-pricing-page">
        {/* Hero */}
        <header className="bxp-hero">
          <span className="bxp-eyebrow">SIMPLE. TRANSPARENT. FLEXIBLE.</span>
          <h1 className="bxp-title">Pricing for Growing Businesses</h1>
          <p className="bxp-subtitle">
            Start free. Upgrade when you need more. No credit card required.
          </p>

          {/* Billing toggle */}
          <div className="bxp-toggle-row">
            <div className="bxp-toggle">
              <button
                type="button"
                className={`bxp-toggle-btn ${billing === "monthly" ? "active" : ""}`}
                onClick={() => setBilling("monthly")}
              >
                Monthly
              </button>
              <button
                type="button"
                className={`bxp-toggle-btn ${billing === "annual" ? "active" : ""}`}
                onClick={() => setBilling("annual")}
              >
                Annual
              </button>
            </div>
            <span className="bxp-save-tag">
              <svg width="28" height="18" viewBox="0 0 28 18" fill="none" className="bxp-save-arrow">
                <path d="M26 17C20 10 14 4 2 2" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" fill="none" />
                <path d="M7 1L2 2L3 7" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
              Save up to 17%
            </span>
          </div>
        </header>

        {/* Cards */}
        <section className="bxp-grid">
          {/* Free */}
          <article className="bxp-card bxp-card--free">
            <div className="bxp-card-head">
              <span className="bxp-card-icon bxp-card-icon--green">
                <Sprout size={22} />
              </span>
              <h2 className="bxp-card-name">Free</h2>
              <p className="bxp-card-desc">For small teams to get started</p>
            </div>
            <div className="bxp-price-block">
              <span className="bxp-price">₹0</span>
              <span className="bxp-price-per">/ month</span>
            </div>
            <span className="bxp-emp-badge">Up to 10 employees</span>
            <ul className="bxp-features">
              {FREE_FEATURES.map((f) => (
                <li key={f}><Check size={15} className="bxp-check" /> {f}</li>
              ))}
            </ul>
            <button
              type="button"
              className="bxp-cta bxp-cta--outline"
              onClick={() => startSignup("free")}
            >
              Get Started Free
            </button>
            <p className="bxp-cta-hint">No credit card required</p>
          </article>

          {/* Premium */}
          <article className="bxp-card bxp-card--premium">
            <span className="bxp-badge">Best Value</span>
            <div className="bxp-card-head">
              <span className="bxp-card-icon bxp-card-icon--blue">
                <Users size={22} />
              </span>
              <h2 className="bxp-card-name">Premium</h2>
              <p className="bxp-card-desc">Complete HRMS & Payroll</p>
            </div>
            <div className="bxp-price-block">
              <span className="bxp-price-mrp">₹{MRP}</span>
              <span className="bxp-price">₹{price}</span>
              <span className="bxp-price-per">/ employee / month</span>
              <span className="bxp-discount-tag">{DISCOUNT_PCT}% OFF</span>
            </div>
            <div className="bxp-min-price">
              {isAnnual ? (
                <>
                  <p>₹{(annualTotal * MIN_EMPLOYEES / 12).toLocaleString("en-IN")} / month for up to {MIN_EMPLOYEES} employees</p>
                  <p>₹{price} for each additional employee / month</p>
                </>
              ) : (
                <>
                  <p>₹{MIN_MONTHLY.toLocaleString("en-IN")} / month for up to {MIN_EMPLOYEES} employees</p>
                  <p>₹{MONTHLY_PRICE} for each additional employee / month</p>
                </>
              )}
            </div>
            <ul className="bxp-features">
              {PREMIUM_FEATURES.map((f) => (
                <li key={f}><Check size={15} className="bxp-check" /> {f}</li>
              ))}
            </ul>
            <button
              type="button"
              className="bxp-cta bxp-cta--solid"
              onClick={() => startSignup("premium")}
            >
              Start Premium
            </button>
            <p className="bxp-cta-hint">No credit card required</p>
          </article>

          {/* Customized */}
          <article className="bxp-card bxp-card--custom">
            <div className="bxp-card-head">
              <span className="bxp-card-icon bxp-card-icon--orange">
                <Building2 size={22} />
              </span>
              <h2 className="bxp-card-name">Customized</h2>
              <p className="bxp-card-desc">For growing & enterprise organizations</p>
            </div>
            <div className="bxp-price-block">
              <span className="bxp-price bxp-price--dark">Contact Sales</span>
            </div>
            <p className="bxp-custom-sub">Tailored to your organization's needs</p>
            <ul className="bxp-features">
              {CUSTOM_FEATURES.map((f) => (
                <li key={f}><Check size={15} className="bxp-check bxp-check--orange" /> {f}</li>
              ))}
            </ul>
            <Link to="/book-demo" className="bxp-cta bxp-cta--outline bxp-cta--orange">
              Contact Sales
            </Link>
          </article>
        </section>

        {/* Feature comparison */}
        <section className="sub-card sub-comparison">
          <h2><Building2 size={18} /> Feature comparison</h2>
          <div className="sub-table-wrap">
            <table className="sub-table sub-comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th>Premium</th>
                  <th>Customized</th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_ROWS.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td className="center">
                      {row.free ? <Check size={16} className="ok" /> : <X size={16} className="no" />}
                    </td>
                    <td className="center">
                      {row.premium ? <Check size={16} className="ok" /> : <X size={16} className="no" />}
                    </td>
                    <td className="center">
                      {row.custom ? <Check size={16} className="ok" /> : <X size={16} className="no" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}
